import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { useGuard } from "../../../hooks/auth/useGuard";
import { useDispatch, useSelector } from "react-redux";
import {
  Appstore,
  toggleSideMenuIsOpen,
  setSideMenuIsOpen,
} from "../../../redux";
import AuthContext from "../../../store/AuthContext";
import ChevronDown from "../../Atomos/Icons/ChevronDown";
import EmailIcon from "../../Atomos/Icons/EmailIcon";
import Tools from "../../Atomos/Icons/Tools";
import Sync from "../../Atomos/Icons/Sync";
import WorldServicesGroupIcon from "../../Atomos/Icons/WorldServicesGroupIcon";
import VCard from "../../Atomos/Icons/ViewMode/VCard";
import Groups from "../../Atomos/Icons/ViewMode/Groups";
import LocalActivity from "../../Atomos/Icons/LocalActivity";
import Bar_chart_4_bars from "../../Atomos/Icons/Bar_chart_4_bars";
import Accounting from "../../Atomos/Icons/Accounting";
import Blogs from "../../Atomos/Icons/Blogs";
import Insights from "../../Atomos/Icons/Insights";
import Resources from "../../Atomos/Icons/Resources";
import WSGLearningInstitute from "../../Atomos/Icons/WSGLearningInstitute";
import IndividualSearch from "../../Atomos/Icons/IndividualSearch";
import Update from "../../Atomos/Icons/Update";
import Catalogs from "../../Atomos/Icons/Catalogs";
import Lists from "../../Atomos/Icons/Lists";
import Categories from "../../Atomos/Icons/Categories";
import New from "../../Atomos/Icons/New";
import ChevronRight from "../../Atomos/Icons/ChevronRight";
import "./Navigation.module.css";
import PersistentLink from "../../Moleculas/PersistentLink";
import Question from "../../Atomos/Icons/Question";
import FacebookCircled from "../../Atomos/Icons/FacebookCircled";
import XCircled from "../../Atomos/Icons/XCircled";
import LinkedInCircled from "../../Atomos/Icons/LinkedInCircled";
import { AccessTypeWithStaff } from "../../../Enums";
import DocumentMagnifying from "../../Atomos/Icons/DocumentMagnifying";
import WhiteBars from "../../Atomos/Icons/WhiteBars";
import MemberSearch from "./MemberSearch";
import ChevronUp from "../../Atomos/Icons/ChevronUp";
import { ReactComponent as UserOutlinedSvg } from "../../../assets/images&icons/user-outlined.svg";
import { ReactComponent as ToolsOutlinedSvg } from "../../../assets/images&icons/tools-outlined.svg";
import {
  HELP_CENTER_PATH,
  HELP_CENTER_STAFF_PATH,
} from "../../../pages/HelpCenter/helpCenterRoutes";
interface MenuItem {
  name: string;
  to?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  requiresWsgSuper?: boolean;
  requiresDataAdmin?: boolean;
}

const SideMenu = () => {
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [activeSubmenu, setActiveSubmenu] = useState<{
    [key: number]: { id: string; top: number; left: number };
  }>({});
  const [canScrollTop, setCanScrollTop] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement>(null);

  const [showMenu, setShowMenu] = useState<boolean>(true);

  const goToTop = () => {
    if (navRef.current) {
      navRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(
    function validateRoute () {
      const excludedRoutes = [
        "/two-factor-auth",
        "/login",
        "/two-factor-acknowledgement",
        "/",
      ] as const;
      if (
        excludedRoutes.includes(
          location.pathname as (typeof excludedRoutes)[number],
        )
      ) {
        setShowMenu(false);
      } else {
        setShowMenu(true);
      }
    },
    [location.pathname],
  );

  const nav = navRef.current;
  useEffect(() => {
    if (!nav) {
      return;
    }

    const handleScroll = () => {
      setCanScrollTop(nav.scrollTop > 0);
    };

    nav.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => nav.removeEventListener("scroll", handleScroll);
  }, [navRef, location.pathname]);

  const { isStaffMode } = useStaffMode();

  const hoverTextColor = isStaffMode
    ? "hover:text-secondary"
    : "hover:text-primary";
  const menuTextColor = () => {
    return isStaffMode ? "text-secondary" : "text-primary";
  };

  const closeAll = () => { };
  const isOpen = useSelector((state: Appstore) => state.menus.sideMenuIsOpen);
  const sideMenuHeight = useSelector(
    (state: Appstore) => state.menus.sideMenuHeight,
  );
  const headerHeight = useSelector(
    (state: Appstore) => state.menus.headerHeight,
  );

  const { sideMenuIsOpen } = useSelector((state: Appstore) => state.menus);

  const { isAuthenticated, tokenDecoded, loggedPerson, _2FAuth } =
    useContext(AuthContext);

  const userPermission =
    tokenDecoded?.extension_WSG_Permissions as AccessTypeWithStaff;
  const { userIsDev } = useGuard();

  const { pathname } = useLocation();

  const memberMenu: MenuItem[] = useMemo(() => {
    const menu: MenuItem[] = [
      {
        to: "/mywsg",
        name: "My WSG",
        icon: <UserOutlinedSvg className="w-4 h-4 md:w-6 md:h-6" />,
      },
      {
        name: "Dashboard",
        icon: <Bar_chart_4_bars />,
        requiresDataAdmin: true,
        children: [
          {
            to: `/wsg_preview_new.asp?action=preview&directory=main&pcomp=${loggedPerson?.firmLegacyId}`,
            name: "Firm Profile",
          },
          { to: "/wsg_firmprofessionals.asp", name: "Professional Profiles" },
          {
            to: `/webcontacts_new.asp?n=${loggedPerson?.firmLegacyId}`,
            name: "Main Contacts for WSG",
          },
          { to: "/manage_publications.asp", name: "Firm Articles" },
          { to: "/manage_releases.asp", name: "Firm Press Releases" },
          { to: "/manage_events.asp", name: "Firm Meetings and Events" },
          {
            to: "/referrals",
            name: "Firm Meetings and Collaborations",
          },
          { to: "/manage_blogrolls.asp", name: "Firm Blogs" },
          { to: "/history.asp", name: "Log Data" },
          { to: "/manage_doingbizguide.asp", name: "Firm Business Guides" },
          {
            to: "/manage_practiceheads.asp",
            name: "Areas of Practice/ Practice Chair",
          },
          { to: "/manage_secondments.asp", name: "Secondment Listings" },
          { to: "/manage_dealmaker.asp", name: "Firm Deals" },
        ],
      },
      {
        to: "/wsg_members.asp",
        name: "WSG Directory",
        icon: <VCard className=" " />,
      },
      { to: "/wsg_groups.asp", name: "WSG Groups", icon: <Groups /> },
      { to: "/events", name: "WSG Events", icon: <LocalActivity /> },
      {
        to: "/wsg-knowledge-center.asp",
        name: "WSG Knowledge Center",
        icon: <DocumentMagnifying />,
      },
      {
        name: "Firm Tools",
        icon: <ToolsOutlinedSvg className="w-4 h-4 md:w-6 md:h-6" />,
        children: [
          { to: "/referrals", name: "Firm Referrals" },
          {
            to: `/publications?id=${loggedPerson?.firm}`,
            name: "Firm Publications",
          },
          {
            to: `/presses?id=${loggedPerson?.firm}`,
            name: "Firm Press Releases",
          },
          // { to: "/eventsbyfirm", name: "Firm Events" },
          { to: "/submitarticle.asp", name: "Submit Articles" },
          { to: "/submitrelease.asp", name: "Submit Press" },
          { to: "/manage_events.asp?action=add", name: "Submit Events" },
        ],
      },
      {
        name: "Insights",
        icon: <Insights />,
        children: [
          { to: "/gcinsights.asp", name: "GC Insights" },
          { to: "/blog", name: "Blog Posts" },
          { to: "/blog/featuredblog.aspx", name: "Featured" },
          { to: "/blog/default.aspx", name: "WSG Blog" },
        ],
      },
      {
        name: "Resources",
        icon: <Resources />,
        children: [
          {
            name: "News & Articles",
            children: [
              { to: "/publications.asp", name: "Articles" },
              { to: "/releases.asp", name: "Press" },
              { to: "/success_stories.asp", name: "WSG Collaborations" },
              { to: "/networkwire_1q2026.asp", name: "WSG Newsletter" },
              { to: "/newsfeeds", name: "Latest World News" },
            ],
          },
          { to: "/secondments.asp", name: "Secondments" },
          { to: "/dealmaker.asp", name: "Dealmakers" },
        ],
      },
      {
        name: "WSG Learning Institute",
        icon: <WSGLearningInstitute />,
        children: [
          {
            to: "/wsglearninginstitute_growthplay.asp",
            name: "WSG Leadership Training Program",
          },
          {
            to: "/marketing-workshop-chamber-strategy",
            name: "WSG Marketing Workshop",
          },
          {
            to: "/wsglearninginstituteoverview.asp",
            name: "WSG and Emeritus Program",
          },
        ],
      },
      { to: "/aboutus", name: "About WSG", icon: <WorldServicesGroupIcon /> },
      { to: HELP_CENTER_PATH, name: "Help", icon: <Question /> },
    ];

    const hideSomeDashboardLinksForWsgStaff =
      userPermission === AccessTypeWithStaff.wsgStaff ||
      userPermission === AccessTypeWithStaff.wsgSuper;
    if (!hideSomeDashboardLinksForWsgStaff) {
      return menu;
    }

    return menu.map((item) => {
      if (!item.requiresDataAdmin || !item.children?.length) {
        return item;
      }
      const nextChildren = item.children.filter((child) => {
        const url = child.to ?? "";
        if (url.includes("wsg_preview_new.asp")) {
          return false;
        }
        const base = url.split("?")[0] ?? "";
        if (base === "/manage_blogrolls.asp") {
          return false;
        }
        if (base === "/manage_practiceheads.asp") {
          return false;
        }
        return true;
      });
      if (nextChildren.length === item.children.length) {
        return item;
      }
      return { ...item, children: nextChildren };
    });
  }, [loggedPerson?.firm, loggedPerson?.firmLegacyId, userPermission]);

  const staffMenu: MenuItem[] = [
    {
      name: "Individual Profile Search",
      to: "/wsg_admin22.asp",
      icon: <IndividualSearch />,
    },
    {
      name: "Members",
      icon: <VCard className="text" />,
      children: [
        { to: "/manageFirm", name: "Manage Firm" },
        { to: "/admin_firmsocialmedia.asp", name: "Firm Score" },
        { to: "/admin_firmpracticeheads.asp", name: "Practice Heads" },
        { to: "/admin_wsgcontacts.asp", name: "WSG Contacts" },
      ],
    },
    { to: "/accounting", name: "Accounting", icon: <Accounting /> },
    { to: "/wsg_admin15.asp", name: "Manage Groups", icon: <Groups /> },
    {
      name: "Manage Events",
      icon: <LocalActivity />,
      children: [
        { to: "/events-staff", name: "Events" },
        { to: "/attendees-registrations", name: "Attendees Registrations" },
      ],
    },
    {
      name: "Content",
      icon: <Resources />,
      children: [
        { to: "/content", name: "Member Content" },
        { to: "/wsg-knowledge-center.asp", name: "Knowledge Center" },
        { to: "/wsg-success-stories", name: "WSG Success Stories" },
        // { to: "/wsg-content", name: "WSG Content" }, RWSG2-93 - Temporary removal (until WSG content is clarified)
        { to: "/member-reports", name: "Member Reports" },
      ],
    },
    {
      name: "Blogs",
      icon: <Blogs />,
      children: [
        { to: "/admin_blogrolls.asp", name: "Blogroll" },
        { to: "/admin_manage_blogposts.asp", name: "Blog Posts" },
      ],
    },
    {
      name: "Email",
      icon: <EmailIcon />,
      children: [
        { to: "/send-email", name: "Send Emails" },
        { to: "/email-design", name: "Design Email" },
        { to: "/email-stats", name: "Email Stats" },
        { to: "/unsubscribed-list", name: "Unsubscribed List" },
      ],
    },
    {
      name: "Site Updates",
      icon: <Update />,
      children: [
        {
          name: "Help Guide",
          children: [
            { to: "/howto_admin.asp", name: "How To" },
            { to: "/faq_admin.asp", name: "FAQ" },
          ],
        },
        { to: "/bodupdate.aspx?action=1", name: "BoD/Regional Council" },
        { to: "/believeupdate.aspx?action=2", name: "WSG Objectives" },
        { to: "/wsg_admin27c.asp", name: "Home Page" },
        { to: "/site-updates/contacts", name: "Contacts" },
      ],
    },
    {
      name: "Tools",
      icon: <ToolsOutlinedSvg />,
      children: [
        { to: "/staff/referrals", name: "Referrals/Collaborations" },
        { to: "/admin_manage_secondments.asp", name: "Secondments" },
        { to: "/admin_manage_dealmaker.asp", name: "Dealmaker" },

        { to: "/wsg_adminguides.asp", name: "Firm Business Guide" },
        { to: "/admin_assessmentfees.asp", name: "Assessment Fee" },
        { to: "/admin_manage_bizguide.asp", name: "Guides" },
        { to: "/listing_edit.asp", name: "Guests/Speaker Lists" },
        {
          to: "/membertomember.aspx?eventid=2437",
          name: "Member - to - Member",
        },
        { to: HELP_CENTER_STAFF_PATH, name: "WSG Help Center" },
        ...(userIsDev() ? [{ to: "/admin/settings", name: "Settings" }] : []),
      ],
    },
    {
      name: "Syncs",
      icon: <Sync />,
      children: [
        { to: "/admin_importmodule.asp", name: "Member Syncs" },
        { to: "/admin_rssfeedsync.asp", name: "Press/Article Sync" },
        { to: "/admin_Rssfeeds.asp", name: "Press/Article RSS Feed" },
        { to: "/integrator-engine/firms", name: "Import Module Dashboard" },
      ],
    },
    {
      name: "Analytics",
      icon: <Bar_chart_4_bars />,
      children: [
        {
          name: "WSG General Stats",
          children: [
            { to: "/dashboard_powerbi.asp?rt=stats", name: "Members" },
            { to: "/dashboard_powerbi.asp?rt=events", name: "Events" },
            { to: "/dashboard_powerbi.asp?rt=referrals", name: "Referrals" },
            { to: "/dashboard_powerbi.asp?rt=dues", name: "Annual Dues" },
          ],
        },
        { to: "/wsg_adminstat.asp", name: "Legacy Statistics" },
        {
          name: "Google Analytics",
          children: [
            { to: "/dashboard_ganalytics.asp?rt=all", name: "WSG Site" },
            { to: "/dashboard_ganalytics.asp?rt=firm", name: "Firm Profile" },
            {
              to: "/dashboard_ganalytics.asp?rt=pro",
              name: "Professional Profile",
            },
            {
              to: "/dashboard_ganalytics.asp?rt=article",
              name: "Article/Press",
            },
            { to: "/dashboard_ganalytics.asp?rt=campaign", name: "Campaigns" },
          ],
        },
        { to: "/dashboard_sendgrid.asp", name: "SendGrid Stats" },
        {
          name: "SEO",
          children: [
            {
              to: "",
              name: "Meta Tags",
              children: [
                { to: "/admin_manage_metadata.asp", name: "Firm Profile" },
                {
                  to: "/admin_professional_metadata.asp",
                  name: "Professional Profile",
                },
              ],
            },
            { to: "/AOP", name: "AOP Mapping" },
            { to: "/AOPRanking", name: "AOP Ranking" },
            { to: "/ActivityLog", name: "Activity Log" },
          ],
        },
      ],
    },
    {
      name: "Catalogs",
      icon: <Catalogs />,
      requiresWsgSuper: true,
      children: [
        { to: "/Catalogs/Address", name: "Address" },
        { to: "/Catalogs/AreaOfPractice", name: "Areas Of Practice" },
        { to: "/Catalogs/AOPRanking", name: "AOP Ranking" },
        { to: "/Catalogs/BarAdmission", name: "Bar Admission" },
        { to: "/Catalogs/City", name: "City" },
        { to: "/Catalogs/Company", name: "Company" },
        { to: "/Catalogs/Content", name: "Content" },
        { to: "/Catalogs/ContentGeneralTopic", name: "Content General Topic" },
        { to: "/Catalogs/PublicationTag", name: "Content Tag" },
        { to: "/Catalogs/Country", name: "Country" },
        { to: "/Catalogs/DataAdmin", name: "Data Admin" },
        { to: "/Catalogs/Degree", name: "Degree" },
        { to: "/Catalogs/Education", name: "Education" },
        { to: "/Catalogs/Firm", name: "Firm" },
        { to: "/Catalogs/FirmNote", name: "Firm Note" },
        { to: "/Catalogs/FirmContact", name: "Firm Contact" },
        { to: "/Catalogs/GlobalCrisis", name: "Global Crisis" },
        { to: "/Catalogs/GuestSpeaker", name: "Guest Speaker" },
        { to: "/Catalogs/Jurisdiction", name: "Jurisdiction" },
        { to: "/Catalogs/JurisdictionGroup", name: "Jurisdiction Group" },
        { to: "/Catalogs/Language", name: "Language" },
        { to: "/Catalogs/MemberMainContact", name: "Member Main Contact" },
        { to: "/Catalogs/Office", name: "Office" },
        { to: "/Catalogs/person", name: "Person" },
        { to: "/Catalogs/PracticeIndustry", name: "Practice Industry" },
        { to: "/Catalogs/Region", name: "Region" },
        { to: "/Catalogs/RepresentativeClient", name: "Representative Client" },
        { to: "/Catalogs/School", name: "School" },
        { to: "/Catalogs/State", name: "State" },
        { to: "/Catalogs/Substate", name: "SubState" },
        { to: "/Catalogs/User", name: "User" },
        { to: "/Catalogs/WSG", name: "WSG" },
      ],
    },
    {
      name: "Lists",
      icon: <Lists />,
      children: [
        { to: "/Catalogs/Company", name: "Company" },
        { to: "/Catalogs/GuestSpeaker", name: "Guest Speaker" },
      ],
    },
    {
      name: "Legacy Misc",
      icon: <Categories />,
      children: [
        { to: "/wsg_admin17.asp", name: "Trigger" },
        { to: "/history_logs.asp", name: "History Logs" },
        { to: "/admin_database.asp", name: "Database" },
      ],
    },
    {
      name: "Beta",
      icon: <New />,
      children: [
        { to: "/admin_notificationemails.asp", name: "Notification Email" },
        { to: "/admin_manage_networkwire.asp", name: "Networkwire Tool" },
      ],
    },
    { to: HELP_CENTER_STAFF_PATH, name: "Help", icon: <Question /> },
  ];

  const dispatch = useDispatch();

  const staffFilteredMenu = staffMenu.filter((item) => {
    if (
      item.requiresWsgSuper &&
      userPermission !== AccessTypeWithStaff.wsgSuper
    ) {
      return false;
    }
    return true;
  });

  const memberFilteredMenu = memberMenu.filter((item) => {
    if (!item.requiresDataAdmin || loggedPerson?.firmAdminAccess) {
      return true;
    }
    return false;
  });

  const filteredMenu = useMemo(() => {
    return isStaffMode ? staffFilteredMenu : memberFilteredMenu;
  }, [isStaffMode, staffFilteredMenu, memberFilteredMenu]);

  useEffect(() => {
    dispatch(setSideMenuIsOpen(false));
    setActiveSubmenu({});
  }, [pathname]);

  const handleMenuItemClick = (path: string, hasUrl: boolean) => {
    setOpenMenus((prev) => {
      if (prev.includes(path)) {
        return prev.filter((p) => !p.startsWith(path));
      }
      return [...prev.filter((p) => path.startsWith(p)), path];
    });
    if (hasUrl) {
      setOpenMenus([]);
      closeAll();
    }
  };

  const handleMouseEnter = (
    event: React.MouseEvent,
    id: string,
    level: number,
    childrenLength: number,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const itemHeight = 36;
    const maxHeight = window.innerHeight * 0.9;
    const submenuHeight = Math.min(
      (childrenLength + 1) * itemHeight,
      maxHeight,
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let topPosition = rect.top;

    if (spaceBelow < 0) {
      topPosition = Math.max(0, window.innerHeight - submenuHeight);
    } else if (spaceBelow < submenuHeight && spaceAbove >= submenuHeight) {
      topPosition = rect.bottom - submenuHeight;
    } else if (spaceBelow < submenuHeight) {
      topPosition = Math.max(
        0,
        Math.min(rect.top, window.innerHeight - submenuHeight),
      );
    }

    setActiveSubmenu((prev) => ({
      ...prev,
      [level]: {
        id,
        top: topPosition,
        left: rect.right,
      },
    }));
  };

  const handleMouseLeave = (level: number) => {
    setActiveSubmenu((prev) => {
      const newEntries = Object.entries(prev).filter(
        ([key]) => Number(key) < level,
      );
      return Object.fromEntries(newEntries);
    });
  };

  const normalize = (url: string) => {
    return url.split(/[?#]/)[0].toLowerCase();
  };

  const activeBackground = (item: MenuItem): boolean => {
    const currentPath = normalize(pathname);

    const isMatch = (menuPath: string): boolean => {
      const base = normalize(menuPath);
      if (currentPath === base) {
        return true;
      }
      if (currentPath.startsWith(base.endsWith("/") ? base : base + "/")) {
        return true;
      }

      return false;
    };

    const isActiveRecursive = (menuItem: MenuItem): boolean => {
      if (menuItem.to && isMatch(menuItem.to)) {
        return true;
      }
      return menuItem.children?.some(isActiveRecursive) ?? false;
    };

    return isActiveRecursive(item);
  };

  const isActiveUrl = (menuUrl: string): boolean => {
    const currentPath = normalize(pathname);
    const base = normalize(menuUrl);

    if (currentPath === base) {
      return true;
    }

    if (currentPath.startsWith(base.endsWith("/") ? base : base + "/")) {
      return true;
    }

    return false;
  };

  const expandedMenu = (item: MenuItem, currentPath: string) => {
    return (
      <>
        {item.to ? (
          <PersistentLink
            to={item.to}
            className={`flex whitespace-nowrap overflow-hidden px-2 md:px-4 py-3 md:py-3 h-10 md:h-12 items-center ${hoverTextColor} hover:scale-105 transition-colors duration-300`}
            resetOnNavigate
          >
            {item.icon && (
              <p className="md:h-6 md:w-6 w-4 h-4 mr-2 md:mr-5">{item.icon}</p>
            )}
            {item.name}
          </PersistentLink>
        ) : (
          <div
            className={`flex whitespace-nowrap overflow-hidden px-2 md:px-4 py-3 md:py-3 h-10 md:h-12 items-center ${hoverTextColor} hover:scale-105 transition-colors duration-300`}
            onClick={() => handleMenuItemClick(currentPath, !!item.to)}
          >
            {item.name ? (
              <>
                {item.icon && (
                  <p className="md:h-6 md:w-6 w-4 h-4 mr-2 md:mr-5">
                    {item.icon}
                  </p>
                )}
                <p className="flex-1 text-start">{item.name}</p>

                <div
                  className={`${isOpen ? "block" : "hidden"} flex whitespace-nowrap overflow-hidden`}
                >
                  {item.children && (
                    <div className="ml-1 w-6 h-6 fill-white">
                      <ChevronDown
                        rotation={
                          openMenus.includes(currentPath)
                            ? "rotate(180deg)"
                            : "rotate(0deg)"
                        }
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex w-full h-1 border-t-2" />
            )}
          </div>
        )}
      </>
    );
  };

  const collapsedMenu = (item: MenuItem, level: number) => {
    const isActive = activeSubmenu[level]?.id === item.name;
    const hasChildren = item.children && item.children.length > 0;

    return (
      <div className="relative cursor-default">
        <div
          className={`flex py-3 md:py-3 px-2 md:px-4 h-10 md:h-12 items-center ${hoverTextColor} hover:scale-105 transition-colors duration-300`}
          onMouseEnter={(event) =>
            handleMouseEnter(
              event,
              item.name,
              level,
              item.children?.length ?? 0,
            )
          }
          onClick={() => {
            if (!hasChildren && item.to) {
              navigate(item.to);
            }
          }}
          style={{ cursor: !hasChildren && item.to ? "pointer" : "default" }}
        >
          {item.icon ? (
            <p className="h-4 md:h-6 w-4 md:w-6">{item.icon}</p>
          ) : (
            <div className="flex w-full h-1 border-t-2" />
          )}
        </div>

        {isActive && submenu(level, item)}
      </div>
    );
  };

  const renderMenuItems = (items: MenuItem[], parentPath = "", level = 0) => {
    return items.map((item, index) => {
      const currentPath = `${parentPath}/${item.name}`;

      return (
        <div key={index} className="flex flex-col w-full">
          <li className={"relative flex w-full cursor-pointer fill-white"}>
            <div
              className={`
                  px-1 relative z-10 w-80
                  ${activeBackground(item) ? `${menuTextColor()}` : "text-gray-font"}`}
              style={{ marginLeft: level * 20 }}
            >
              {isOpen
                ? expandedMenu(item, currentPath)
                : collapsedMenu(item, level)}
            </div>
          </li>
          {item.children && isOpen && (
            <div
              className={`flex flex-col transition-all duration-300 ease-in-out transform ${openMenus.includes(currentPath)
                ? "max-h-[4000px] opacity-100"
                : "max-h-0 opacity-0"
                } overflow-hidden`}
            >
              {renderMenuItems(item.children, currentPath, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const submenu = (level: number, item: MenuItem) => {
    return (
      <ul
        className={` ${item.children && "rounded-r-lg text-center"}
          fixed w-fit text-gray-font text-smm max-h-[85vh] shadow-md shadow-black/50 ml-[2px] bg-white rounded-r-lg overflow-visible`}
        style={{
          top: activeSubmenu[level]?.top,
          left: activeSubmenu[level]?.left,
        }}
        onMouseLeave={() => handleMouseLeave(level)}
      >
        {item.children && (
          <li
            className={`${isStaffMode ? "bg-secondary" : "bg-primary-dark"} sticky top-0 z-60 text-white text-smm rounded-r-lg overflow-hidden cursor-default`}
          >
            <div className="w-full">{item.name}</div>
          </li>
        )}

        {item.to && level === 0
          ? submenuItem(item.name, item.to)
          : item.children?.map((child) => (
            <li
              className="overflow-visible"
              key={child.name}
              onMouseEnter={(event) =>
                handleMouseEnter(
                  event,
                  child.name,
                  level + 1,
                  child.children?.length || 0,
                )
              }
            >
              {submenuItem(child.name, child.to)}
              {activeSubmenu[level + 1]?.id === child.name &&
                submenu(level + 1, child)}
            </li>
          ))}
      </ul>
    );
  };

  const submenuItem = (name: string, to?: string) => {
    return (
      <div className="relative group/sub">
        {to ? (
          <PersistentLink
            to={to}
            className={`${hoverTextColor} ${isActiveUrl(to) && menuTextColor()}
              w-full flex items-center hover:scale-105 transition-colors duration-300 px-4 py-2 `}
            resetOnNavigate
          >
            {name}
          </PersistentLink>
        ) : (
          <div
            className={`flex w-full items-center ${hoverTextColor} hover:scale-105 transition-colors duration-300 px-4 py-2`}
          >
            <div className="w-full flex items-center">{name}</div>
            <div className="h-4 w-4">
              <ChevronRight />
            </div>
          </div>
        )}
      </div>
    );
  };

  const toggleMenu = () => {
    dispatch(toggleSideMenuIsOpen());
  };

  const renderSocialMedia = () => {
    const socialMedia = [
      {
        name: "Facebook",
        href: "https://www.facebook.com/worldservicesgroup",
        icon: <FacebookCircled className="w-6 h-6 fill-current" />,
      },
      {
        name: "Twitter",
        href: "https://twitter.com/worldservsgroup",
        icon: <XCircled className="w-6 h-6 fill-current" />,
      },
      {
        name: "LinkedIn",
        href: "https://www.linkedin.com/company/worldservicesgroup",
        icon: <LinkedInCircled className="w-6 h-6 fill-current" />,
      },
    ];

    return socialMedia.map((item) => (
      <a
        key={item.name}
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-gray-font ${hoverTextColor} hover:scale-105 transition-colors duration-200 ease-in-out`}
      >
        {item.icon}
      </a>
    ));
  };

  if (!isAuthenticated) {
    return null;
  }

  if (
    loggedPerson?.twoFactorAuthentication?.status &&
    !_2FAuth &&
    isAuthenticated
  ) {
    return null;
  }

  return showMenu ? (
    <>
      <div
        className="absolute left-0 bg-none w-full z-20 md:z-40 pointer-events-none"
        style={{ top: headerHeight }}
      >
        <div
          className={`
      h-full shrink-0 ${isOpen ? "w-64 md:w-80" : "w-12 md:w-24"}
      flex flex-col transition-all duration-300 ease-in-out text-white pointer-events-auto`}
          style={{ height: sideMenuHeight }}
        >
          <nav
            ref={navRef}
            className="bg-white z-60 flex flex-col overflow-y-scroll overflow-x-hidden mx-1 md:mx-4 my-3 rounded-xl md:rounded-3xl border-black border-y-emerald-100 h-full shadow-md shadow-black/50"
            onMouseLeave={() => handleMouseLeave(0)}
          >
            <div className="flex flex-1 flex-col">
              <div className="sticky top-0 z-20 bg-gray-100 rounded-xl md:rounded-3xl">
                <button
                  className="text-gray-font md:py-4 pl-3 md:pl-5 w-full flex h-10 md:h-16 items-center"
                  onClick={toggleMenu}
                >
                  <div className="w-4 h-4 md:w-6 md:h-6 text-gray-font flex-shrink-0">
                    <WhiteBars />
                  </div>
                  <p
                    className={`ml-4 transition-opacity duration-300 font-bold text-xl ${isOpen ? "opacity-100" : "opacity-0"}`}
                  >
                    Menu
                  </p>
                </button>
              </div>

              {renderMenuItems(filteredMenu)}

              {isOpen && (
                <div className="flex justify-evenly items-end p-8 flex-1 transition-opacity duration-300 text-gray-font">
                  {renderSocialMedia()}
                </div>
              )}
            </div>
            <button
              className={`py-1 justify-center w-full flex sticky bottom-0 z-20 ${canScrollTop ? menuTextColor() : "text-gray-200"}  rounded-b-3xl bg-white/80`}
              onClick={goToTop}
              disabled={!canScrollTop}
            >
              <div className="w-6 h-6 ">
                <ChevronUp />
              </div>
            </button>
          </nav>
        </div>
        <div
          className={`${sideMenuIsOpen ? "w-[calc(100%-270px)] md:w-[calc(100%-350px)]" : "w-[calc(100%-60px)] md:w-[calc(100%-150px)]"}
        bg-transparent absolute right-2 md:right-10 -top-3 flex flex-col transition-all duration-300 ease-in-out items-stretch z-30 md:z-70`}
        >
          <MemberSearch />
        </div>
      </div>
    </>
  ) : null;
};

export default SideMenu;
